import { generatePath, Outlet, useNavigate, useParams } from "react-router-dom";
import { Formik, Form } from "formik";
import useCheckoutSteps, { ICheckout } from "@/pages/checkout/hooks/useCheckoutSteps";
import useSendOrder from "@/pages/checkout/hooks/useSendOrder";
import { AppBar, IconButton, LinearProgress, Stack, Toolbar, Typography, useTheme } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import routes from "@/constants/routes";
import { useContext, useEffect, useMemo } from "react";
import useUserProfile from "@/hooks/useUserProfile";
import { Organization, User } from "@/types";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon } from "@hugeicons/core-free-icons";
import useRoutes from "@/hooks/useRoutes";

export type CheckoutSharedProps = {
  activeUser: User | null;
  organization?: Organization | null;
};

type Params = {
  organizationAlias: string | undefined;
  orderId: string | undefined;
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const sendOrder = useSendOrder();
  const [goToUrl] = useRoutes();
  const { activeUser } = useUserProfile();
  const { palette } = useTheme();
  const { organization } = useContext(WebsiteContext);
  const { organizationAlias, orderId } = useParams<Params>();
  const { stepsProgress, stepId, steps, previousStep, nextStep, isFirstStep, isLastStep, initialValues, validationSchema, activeStep } = useCheckoutSteps();

  const sharedProps: CheckoutSharedProps = useMemo(() => {
    return { activeUser, organization };
  }, [activeUser, organization]);

  const onSubmit = (values: ICheckout) => {
    if (isLastStep) {
      sendOrder(values);
      return;
    }

    // execute step tasks
    if (activeStep?.onSubmit) {
      activeStep.onSubmit(values, sharedProps);
    }

    // continue
    if (organizationAlias) {
      navigate(
        generatePath(routes.organizations.order.checkoutForm, {
          organizationAlias,
          orderId,
          formId: nextStep.id,
        }) + location.search
      );
    } else {
      navigate(routes.checkoutForm.replace(":orderId", orderId ?? "").replace(":formId", nextStep.id) + location.search);
    }
  };

  const onBack = () => {
    if (isFirstStep) {
      if (organizationAlias) {
        goToUrl("organizations.menu.main");
      } else {
        navigate(routes.menus);
      }
    } else {
      if (organizationAlias) {
        navigate(
          generatePath(routes.organizations.order.checkoutForm, {
            organizationAlias,
            orderId,
            formId: previousStep.id,
          }) + location.search
        );
      } else {
        navigate(routes.checkoutForm.replace(":orderId", orderId ?? "").replace(":formId", previousStep.id) + location.search);
      }
    }
  };

  useEffect(() => {
    if (stepId === "checkout") {
      if (organizationAlias) {
        navigate(
          generatePath(routes.organizations.order.checkoutForm, {
            organizationAlias,
            orderId,
            formId: steps[0].id,
          }) + location.search
        );
      } else {
        navigate(routes.checkoutForm.replace(":orderId", orderId ?? "").replace(":formId", steps[0].id) + location.search);
      }
    }
  }, [navigate, orderId, organizationAlias, stepId, steps]);

  if (!activeStep) {
    return <p style={{ color: "white" }}>Loading data...</p>;
  }

  return (
    <Stack height="100dvh" sx={{ backgroundColor: "#DCEAFD" }}>
      <AppBar position="static" color="default" elevation={2} sx={{ zIndex: 100 }}>
        <Toolbar sx={{ py: 1 }}>
          <IconButton size="small" edge="start" color="inherit" aria-label="menu" sx={{ ml: -0.5, mr: 1 }} onClick={onBack}>
            <HugeiconsIcon icon={ArrowLeft02Icon} size={24} color={palette.primary.main} />
          </IconButton>
          <Typography variant="h4" width="100%">
            {activeStep.title}
          </Typography>
          {activeStep?.icon ? <HugeiconsIcon icon={activeStep.icon} size={32} color={palette.primary.main} /> : null}
        </Toolbar>
      </AppBar>
      <LinearProgress variant="determinate" value={stepsProgress} sx={{ height: 6 }} />
      <Formik onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema} enableReinitialize={true}>
        {() => {
          return (
            <Form style={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
              <Stack
                flex={1}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Stack
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    pb: 14,
                  }}
                >
                  <Outlet context={{ steps }} />
                </Stack>

                <Stack
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    p: 2,
                    zIndex: 10,
                  }}
                >
                  <LoadingButton
                    size="large"
                    type="submit"
                    variant="contained"
                    fullWidth
                    color={isLastStep ? "success" : "primary"}
                  >
                    {isLastStep ? "Finalizar" : "Continuar"}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </Stack>
  );
};

export default CheckoutPage;
